import React, { useState } from 'react';
import { ArrowRight, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EnquiryForm = () => {
  const [form, setForm] = useState({
    fullName: '',
    companyName: '',
    mobileNumber: '',
    email: '',
    activeSites: '',
    note: '',
    honeypot: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setStatus({ type: null, message: '' });

    const fullName = form?.fullName?.trim();
    const companyName = form?.companyName?.trim();
    const mobileNumber = form?.mobileNumber?.trim();
    const email = form?.email?.trim();
    const activeSites = parseInt(form?.activeSites, 10);

    if (!fullName || !companyName || !mobileNumber || !email || !form?.activeSites) {
      setStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    if (!Number.isFinite(activeSites) || activeSites < 1) {
      setStatus({ type: 'error', message: 'Number of active sites must be at least 1.' });
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-enquiry', {
        body: {
          fullName,
          companyName,
          mobileNumber,
          email,
          activeSites,
          note: form?.note?.trim(),
          honeypot: form?.honeypot,
        },
      });
      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to send');
      }
      setStatus({ type: 'success', message: "Thanks for getting in touch! We'll be in touch shortly." });
      setForm({ fullName: '', companyName: '', mobileNumber: '', email: '', activeSites: '', note: '', honeypot: '' });
    } catch (err) {
      setStatus({ type: 'error', message: err?.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 transition-colors';

  return (
    <section id="enquiry" className="py-24 lg:py-32 bg-slate-950 border-t border-slate-800">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/20 mb-4">
            <Mail className="w-6 h-6 text-emerald-400" />
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">Get in touch</h2>
          <p className="text-slate-400 text-base">
            Questions about SiteProof? Tell us a bit about your sites and we'll get back to you.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-6 sm:p-8 space-y-4"
          noValidate
        >
          {/* Honeypot — hidden from real users, bots fill everything */}
          <div className="hidden" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form?.honeypot}
                onChange={setField('honeypot')}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="enq-name" className="block text-sm font-semibold text-slate-200 mb-1.5">
                Full name <span className="text-red-400">*</span>
              </label>
              <input
                id="enq-name"
                type="text"
                required
                value={form?.fullName}
                onChange={setField('fullName')}
                placeholder="Jane Smith"
                className={inputCls}
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="enq-company" className="block text-sm font-semibold text-slate-200 mb-1.5">
                Company name <span className="text-red-400">*</span>
              </label>
              <input
                id="enq-company"
                type="text"
                required
                value={form?.companyName}
                onChange={setField('companyName')}
                placeholder="Acme Construction Ltd"
                className={inputCls}
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="enq-mobile" className="block text-sm font-semibold text-slate-200 mb-1.5">
                Mobile number <span className="text-red-400">*</span>
              </label>
              <input
                id="enq-mobile"
                type="tel"
                required
                value={form?.mobileNumber}
                onChange={setField('mobileNumber')}
                placeholder="07700 900000"
                className={inputCls}
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="enq-email" className="block text-sm font-semibold text-slate-200 mb-1.5">
                Email address <span className="text-red-400">*</span>
              </label>
              <input
                id="enq-email"
                type="email"
                required
                value={form?.email}
                onChange={setField('email')}
                placeholder="you@company.com"
                className={inputCls}
                disabled={submitting}
              />
            </div>
          </div>

          <div>
            <label htmlFor="enq-sites" className="block text-sm font-semibold text-slate-200 mb-1.5">
              Number of active sites <span className="text-red-400">*</span>
            </label>
            <input
              id="enq-sites"
              type="number"
              min="1"
              required
              value={form?.activeSites}
              onChange={setField('activeSites')}
              placeholder="e.g. 5"
              className={inputCls}
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="enq-note" className="block text-sm font-semibold text-slate-200 mb-1.5">
              Note <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <textarea
              id="enq-note"
              rows={4}
              value={form?.note}
              onChange={setField('note')}
              placeholder="Anything specific you'd like us to know?"
              className={`${inputCls} resize-y`}
              disabled={submitting}
            />
          </div>

          {status?.type === 'success' && (
            <div className="flex items-start gap-2 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-200">{status?.message}</p>
            </div>
          )}
          {status?.type === 'error' && (
            <div className="flex items-start gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-200">{status?.message}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-8 py-4 rounded-xl text-base transition-all duration-200 shadow-xl shadow-emerald-500/30"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send Enquiry
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default EnquiryForm;
